import React from 'react';
import {
  PageHeader, Card, Button, Select, SkeletonText, ErrorState, useTheme,
} from '@parsina/framework';
import { THEME_PRESETS, FONT_SIZES } from '@parsina/framework/tokens';
import { LogOut } from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useMe } from '../../hooks/useMe.js';
import { logout } from '../../lib/auth.js';
import { fmtParseh } from '../../constants/pricing';
import { IDENTITY_FIELDS, identityValues } from './Profile.shared.js';
import ProfileIdentityForm from './ProfileIdentityForm.jsx';

// پروفایل — the desktop profile.
//
// Three sections: the profile the student typed at onboarding and can fix here
// (core.md §7.8), the account facts other services own, and how this browser
// paints them. Nothing about a clinic, a licence or a کد نظام پزشکی — a student
// has none of those, and the profile IS the gate (core.md §4.2).

export default function ProfileWeb() {
  const { t, locale, setLocale, formatNumber, formatDigits } = useTranslation();
  const { me, balance, loading, error, reload } = useMe();
  const { theme, fontSize, setAppearance } = useTheme();

  const values = identityValues({ me, balance, t, formatNumber, formatDigits, fmtParseh });

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg, overflow: 'hidden' }}>
      <PageHeader title={t('profile.title')}>
        <Button variant="outline" onClick={() => logout()} data-testid="profile-logout">
          {/* Not mirrored: the arrow points out of a door, not along the reading
              direction. */}
          <LogOut size={15} aria-hidden />
          {t('profile.logout')}
        </Button>
      </PageHeader>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 32px 40px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, maxWidth: 900 }}>
          {/* ── مشخصات (editable) ── */}
          <ProfileIdentityForm phone={me?.phone || ''} />

          {/* ── حساب کاربری (derived, read-only) ── */}
          <Card style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: C.textPrimary }}>
              {t('profile.sections.account')}
            </h2>

            {error ? (
              <ErrorState
                title={t('errors.loadFailed')}
                onRetry={reload}
                retryLabel={t('common.actions.retry')}
              />
            ) : loading && !me ? (
              <SkeletonText lines={5} />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {IDENTITY_FIELDS.map((field) => (
                  <div
                    key={field.id}
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 12,
                      padding: '9px 0',
                      borderBottom: `1px solid ${C.border}`,
                    }}
                  >
                    <span style={{ fontSize: 12, color: C.textMuted, flexShrink: 0 }}>
                      {t(field.labelKey)}
                    </span>
                    <span
                      {...(field.ltr && values[field.id] ? { dir: 'ltr' } : {})}
                      style={{
                        marginInlineStart: 'auto',
                        textAlign: 'end',
                        fontSize: 13,
                        fontWeight: 700,
                        color: values[field.id] ? C.textPrimary : C.textMuted,
                        minWidth: 0,
                        overflowWrap: 'anywhere',
                        ...(field.ltr && values[field.id] ? { unicodeBidi: 'isolate' } : {}),
                      }}
                    >
                      {values[field.id] || t('common.labels.none')}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Nothing above is editable here, and this says where it is. */}
            <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.9, color: C.textMuted }}>
              {t('profile.identity.note')}
            </p>
          </Card>

          {/* ── ظاهر و زبان ── */}
          <Card style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: C.textPrimary }}>
              {t('profile.sections.appearance')}
            </h2>

            <div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: CT.ink2, marginBottom: 8 }}>
                {t('profile.appearance.theme')}
              </div>
              <div role="radiogroup" aria-label={t('profile.appearance.theme')} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {THEME_PRESETS.map((preset) => {
                  const active = preset.id === theme;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setAppearance({ theme: preset.id })}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        minHeight: 44,
                        padding: '0 12px',
                        border: `1.5px solid ${active ? C.teal : C.border}`,
                        borderRadius: 10,
                        background: C.card,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 12.5,
                        fontWeight: active ? 800 : 500,
                        color: active ? CT.accentInk : C.textSecondary,
                      }}
                    >
                      {/* The swatches are literal hex in the kit ON PURPOSE: the
                          picker paints every option at once, so it cannot read
                          the active theme's CSS variables. */}
                      <span
                        aria-hidden
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${preset.swatch2}, ${preset.swatch})`,
                        }}
                      />
                      {/* The kit's own labels are Persian-only literals, so the
                          preset id is translated here instead. */}
                      {t(`profile.theme.${preset.id}`)}
                    </button>
                  );
                })}
              </div>
            </div>

            <Select
              label={t('profile.appearance.fontSize')}
              value={fontSize}
              onChange={(e) => setAppearance({ fontSize: e.target.value })}
              options={FONT_SIZES.map((f) => ({ value: f.id, label: t(`profile.fontSize.${f.id}`) }))}
              placeholder=""
            />

            <Select
              label={t('profile.appearance.language')}
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              options={[
                { value: 'fa', label: t('profile.language.fa') },
                { value: 'en', label: t('profile.language.en') },
              ]}
              placeholder=""
            />

            {/* Said plainly: these live in this browser and do not follow the
                student to another device. The contract has no mutation that could
                make them, and pretending otherwise would be a promise the next
                laptop breaks. */}
            <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.9, color: C.textMuted }}>
              {t('profile.appearance.localOnly')}
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
